import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, DropdownItemProps, DropdownProps, Grid, Label } from 'semantic-ui-react';

import { FieldLabel } from '../../../../styles';
import { listProperties, PropertyWalletItemDTO } from '../../../../services/properties';
import { maskZipCode } from '../../../../services/masks';
import { InitialAddressType } from '../../../iframe/fire-insurance-budget';
import { mapWalletPropertyTypeToHireType } from '../../../bond-insurance-budget-form/utils/mapWalletPropertyTypeToHireType';

export type FireWalletSelection = {
  propertyPublicId: string;
  address: InitialAddressType;
  propertyType?: string;
  identification?: string;
};

type Props = {
  estateId?: string;
  value?: FireWalletSelection | null;
  onSelect: (item: FireWalletSelection | null) => void;
};

const getAddressLabel = (item: PropertyWalletItemDTO): string => {
  if (item?.address?.formatted) return item.address.formatted;

  return [
    item?.address?.street,
    item?.address?.number,
    item?.address?.complement,
    item?.address?.neighborhood,
    item?.address?.city,
    item?.address?.state
  ]
    .filter(Boolean)
    .join(', ');
};

const mapItemToSelection = (item: PropertyWalletItemDTO): FireWalletSelection => ({
  propertyPublicId: item.publicId,
  identification: item.identification,
  propertyType: mapWalletPropertyTypeToHireType(item),
  address: {
    street: item.address?.street || null,
    number: item.address?.number || null,
    complement: item.address?.complement || null,
    neighborhood: item.address?.neighborhood || null,
    city: item.address?.city || null,
    state: item.address?.state || null,
    zipCode: maskZipCode(item.address?.zipcode || '')
  }
});

const FirePropertyWalletSearchDropdown = ({
  estateId,
  value,
  onSelect
}: Props): ReactElement | null => {
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<PropertyWalletItemDTO[]>([]);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!estateId) {
      setItems([]);
      return;
    }

    const timeout = setTimeout(async () => {
      requestIdRef.current += 1;
      const currentRequestId = requestIdRef.current;
      setIsLoading(true);

      try {
        const response = await listProperties({
          estateId,
          search: search.trim() || undefined,
          page: 1,
          limit: 10
        });

        if (currentRequestId === requestIdRef.current) {
          setItems(response.items || []);
        }
      } catch (_error) {
        if (currentRequestId === requestIdRef.current) {
          setItems([]);
        }
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [estateId, search]);

  const itemsByPublicId = useMemo(() => {
    const hash = new Map<string, PropertyWalletItemDTO>();
    items.forEach(item => {
      if (item?.publicId) hash.set(item.publicId, item);
    });
    return hash;
  }, [items]);

  const options: DropdownItemProps[] = items.map(item => ({
    key: item.publicId,
    value: item.publicId,
    text: item.identification || 'Sem código do imóvel',
    description: getAddressLabel(item)
  }));

  if (!estateId) return null;

  return (
    <Grid.Row>
      <Grid.Column mobile={16} tablet={16} computer={16}>
        <FieldLabel>PESQUISAR NA CARTEIRA DE IMÓVEIS</FieldLabel>
        <Dropdown
          fluid
          selection
          clearable
          search
          loading={isLoading}
          options={options}
          value={value?.propertyPublicId || ''}
          noResultsMessage="Nenhum imóvel encontrado."
          placeholder="Busque por CEP, bairro, logradouro ou código do imóvel"
          onSearchChange={(_event, data: DropdownProps) => {
            setSearch(String(data.searchQuery || ''));
          }}
          onChange={(_event, data: DropdownProps) => {
            const selectedId = String(data.value || '');

            if (!selectedId) {
              onSelect(null);
              return;
            }

            const selected = itemsByPublicId.get(selectedId);
            if (!selected) return;

            onSelect(mapItemToSelection(selected));
          }}
        />
        {value?.propertyPublicId && (
          <Label color="teal" style={{ marginTop: 8 }}>
            Imóvel da carteira
          </Label>
        )}
      </Grid.Column>
    </Grid.Row>
  );
};

export default FirePropertyWalletSearchDropdown;
